import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { currencyApi, categoryApi, authApi, sheetsApi } from '../lib/api';
import type { Category, CustomCurrency, TransactionType } from '@asset-mgmt/shared';
import type { SessionInfo, SheetsStatus } from '../lib/api';
import { useToast } from '../context/ToastContext';

const CURRENCIES = [
  'USD', 'EUR', 'GBP', 'JPY', 'CNY', 'VND', 'SGD', 'THB', 'KRW',
  'AUD', 'CAD', 'CHF', 'HKD', 'INR', 'MYR', 'IDR', 'PHP', 'TWD',
];

export default function Settings() {
  const { user, googleLinked, hasPassword, updateSettings, updateProfile, logout, refreshUser } = useAuth();
  const toast = useToast();
  const isViewer = user?.role === 'viewer';
  const [baseCurrency, setBaseCurrency] = useState(user?.settings.baseCurrency ?? 'USD');
  const [theme, setTheme] = useState(user?.settings.theme ?? 'system');
  const [saving, setSaving] = useState(false);
  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [profileSaving, setProfileSaving] = useState(false);
  const [telegramUrl, setTelegramUrl] = useState<string | null>(null);
  const [telegramToken, setTelegramToken] = useState<string | null>(null);
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);

  // ── Security state ───────────────────────────────────────────────────────────
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [unlinkingGoogle, setUnlinkingGoogle] = useState(false);
  const [unlinkingTelegram, setUnlinkingTelegram] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deletingAccount, setDeletingAccount] = useState(false);

  // ── Custom Currencies ────────────────────────────────────────────────────────
  const [customCurrencies, setCustomCurrencies] = useState<CustomCurrency[]>([]);
  const [ccForm, setCcForm] = useState({ code: '', name: '', symbol: '', baseCode: 'VND', multiplier: '1000' });
  const [ccSaving, setCcSaving] = useState(false);
  const [ccError, setCcError] = useState<string | null>(null);

  // ── Categories ───────────────────────────────────────────────────────────────
  const [categories, setCategories] = useState<Category[]>([]);
  const [catForm, setCatForm] = useState<{ name: string; color: string; icon: string; type: TransactionType | 'any' }>(
    { name: '', color: '#6b7280', icon: '', type: 'expense' },
  );
  const [catSaving, setCatSaving] = useState(false);
  const [catError, setCatError] = useState<string | null>(null);

  // ── Google Sheets ────────────────────────────────────────────────────────────
  const [sheetsStatus, setSheetsStatus] = useState<SheetsStatus | null>(null);
  const [sheetsLoading, setSheetsLoading] = useState(false);
  const [sheetsSyncing, setSheetsSyncing] = useState(false);
  const [sheetsDisconnecting, setSheetsDisconnecting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    currencyApi.listCustom().then(setCustomCurrencies).catch(() => {});
    categoryApi.list().then(setCategories).catch(() => {});
    // Load sessions for the Security section
    setSessionsLoading(true);
    authApi.getSessions().then(setSessions).catch(() => {}).finally(() => setSessionsLoading(false));
    // Load Sheets status
    setSheetsLoading(true);
    sheetsApi.status().then(setSheetsStatus).catch(() => {}).finally(() => setSheetsLoading(false));
    // Handle ?sheets= query param from OAuth redirect
    const sheetsParam = searchParams.get('sheets');
    if (sheetsParam === 'connected') {
      toast.success('Google Sheets connected! Your data is syncing in the background.');
      setSearchParams((p) => { p.delete('sheets'); return p; }, { replace: true });
    } else if (sheetsParam === 'error') {
      const reason = searchParams.get('reason') ?? 'unknown';
      toast.error(`Google Sheets connection failed: ${reason.replace(/_/g, ' ')}.`);
      setSearchParams((p) => { p.delete('sheets'); p.delete('reason'); return p; }, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync local state when user loads
  useEffect(() => {
    if (user) {
      setBaseCurrency(user.settings.baseCurrency);
      setTheme(user.settings.theme);
      setDisplayName(user.displayName);
    }
  }, [user?.id]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      await updateSettings({ baseCurrency, theme });
      toast.success('Preferences saved.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save preferences.');
    }
    setSaving(false);
  }

  async function handleProfileSave(e: React.FormEvent) {
    e.preventDefault();
    setProfileSaving(true);
    try {
      await updateProfile({ displayName });
      toast.success('Profile updated.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update profile.');
    }
    setProfileSaving(false);
  }

  async function generateTelegramLink() {
    setTelegramLoading(true);
    const result = await authApi.telegramLink().catch(() => ({ url: null, token: null }));
    setTelegramUrl(result.url);
    setTelegramToken(result.token);
    setTelegramLoading(false);
  }

  async function copyCommand() {
    if (!telegramToken) return;
    await navigator.clipboard.writeText(`/link ${telegramToken}`);
    setTokenCopied(true);
    setTimeout(() => setTokenCopied(false), 2500);
  }

  async function handleAddCurrency(e: React.FormEvent) {
    e.preventDefault();
    setCcSaving(true);
    setCcError(null);
    try {
      const created = await currencyApi.createCustom({
        code: ccForm.code,
        name: ccForm.name,
        ...(ccForm.symbol ? { symbol: ccForm.symbol } : {}),
        baseCode: ccForm.baseCode,
        multiplier: parseFloat(ccForm.multiplier),
      });
      setCustomCurrencies((prev) => [...prev, created]);
      setCcForm({ code: '', name: '', symbol: '', baseCode: 'VND', multiplier: '1000' });
      toast.success(`Custom currency "${created.code}" added.`);
    } catch (err) {
      setCcError(err instanceof Error ? err.message : 'Failed to save');
    }
    setCcSaving(false);
  }

  async function handleDeleteCurrency(id: string) {
    if (!confirm('Delete this custom currency?')) return;
    try {
      await currencyApi.deleteCustom(id);
      setCustomCurrencies((prev) => prev.filter((c) => c.id !== id));
      toast.success('Custom currency deleted.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete currency.');
    }
  }

  async function handleAddCategory(e: React.FormEvent) {
    e.preventDefault();
    setCatSaving(true);
    setCatError(null);
    try {
      const created = await categoryApi.create({
        name: catForm.name,
        color: catForm.color,
        ...(catForm.icon ? { icon: catForm.icon } : {}),
        type: catForm.type,
      });
      setCategories((prev) => [...prev, created]);
      setCatForm({ name: '', color: '#6b7280', icon: '', type: catForm.type });
      toast.success(`Category "${created.name}" added.`);
    } catch (err) {
      setCatError(err instanceof Error ? err.message : 'Failed to save');
    }
    setCatSaving(false);
  }

  async function handleDeleteCategory(id: string) {
    if (!confirm('Delete this category? Existing transactions keep their data but will show as "Uncategorized".')) return;
    try {
      await categoryApi.remove(id);
      setCategories((prev) => prev.filter((cat) => cat.id !== id));
      toast.success('Category deleted.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete category.');
    }
  }

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault();
    setPwError(null);
    if (pwForm.next !== pwForm.confirm) {
      setPwError('New passwords do not match');
      return;
    }
    if (pwForm.next.length < 8) {
      setPwError('Password must be at least 8 characters');
      return;
    }
    setPwSaving(true);
    try {
      await authApi.changePassword({ currentPassword: pwForm.current, newPassword: pwForm.next });
      setPwForm({ current: '', next: '', confirm: '' });
      toast.success('Password changed successfully.');
    } catch (err) {
      setPwError(err instanceof Error ? err.message : 'Failed to change password');
    }
    setPwSaving(false);
  }

  async function handleRevokeSession(sessionId: string) {
    try {
      await authApi.revokeSession(sessionId);
      setSessions((prev) => prev.filter((s) => s.sessionId !== sessionId));
      toast.success('Session revoked.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to revoke session.');
    }
  }

  async function handleUnlinkGoogle() {
    if (!confirm('Unlink your Google account? You must have a password to log in.')) return;
    setUnlinkingGoogle(true);
    try {
      await authApi.unlinkGoogle();
      await refreshUser();
      toast.success('Google account unlinked.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to unlink Google');
    }
    setUnlinkingGoogle(false);
  }

  async function handleUnlinkTelegram() {
    if (!confirm('Unlink your Telegram account?')) return;
    setUnlinkingTelegram(true);
    try {
      await authApi.unlinkTelegram();
      await refreshUser();
      toast.success('Telegram account unlinked.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to unlink Telegram');
    }
    setUnlinkingTelegram(false);
  }

  async function handleSheetsSync() {
    setSheetsSyncing(true);
    try {
      await sheetsApi.sync();
      toast.success('Sync started — your spreadsheet will update shortly.');
      // Refresh status to show updated lastSyncAt
      sheetsApi.status().then(setSheetsStatus).catch(() => {});
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Sync failed.');
    }
    setSheetsSyncing(false);
  }

  async function handleSheetsDisconnect() {
    if (!confirm('Disconnect Google Sheets? Your spreadsheet will remain on Google Drive.')) return;
    setSheetsDisconnecting(true);
    try {
      await sheetsApi.disconnect();
      setSheetsStatus({ connected: false });
      toast.success('Google Sheets disconnected.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to disconnect.');
    }
    setSheetsDisconnecting(false);
  }

  async function handleDeleteAccount(e: React.FormEvent) {
    e.preventDefault();
    if (deleteConfirmText !== 'DELETE') {
      setDeleteError('Type DELETE to confirm');
      return;
    }
    setDeletingAccount(true);
    setDeleteError(null);
    try {
      await authApi.deleteAccount({ ...(deletePassword ? { password: deletePassword } : {}) });
      await logout();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete account');
      setDeletingAccount(false);
    }
  }

  return (
    <div className="p-4 sm:p-6">
      <h1 className="mb-4 sm:mb-6 text-2xl font-bold text-gray-900 dark:text-gray-100">Settings</h1>

      <div className="max-w-lg space-y-6">
        {/* Preferences */}
        <Section title="Preferences">
          <form onSubmit={(e) => void handleSave(e)} className="space-y-4">
            <Field label="Base Currency">
              <select
                value={baseCurrency}
                onChange={(e) => setBaseCurrency(e.target.value)}
                className={inp}
              >
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <p className="mt-1 text-xs text-gray-400">
                All totals and charts will display in this currency.
              </p>
            </Field>
            <Field label="Theme">
              <select value={theme} onChange={(e) => setTheme(e.target.value as typeof theme)} className={inp}>
                <option value="system">System default</option>
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </select>
            </Field>
            <button
              type="submit"
              disabled={saving || isViewer}
              title={isViewer ? 'Viewers cannot change settings' : undefined}
              className="rounded-lg bg-primary-600 px-5 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </form>
        </Section>

        {/* Telegram Integration */}
        <Section title="Telegram Bot">
          {user?.settings.telegramLinked ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-emerald-600">
                <span>✓</span> Telegram account linked
              </div>
              <button
                onClick={() => void handleUnlinkTelegram()}
                disabled={unlinkingTelegram}
                className="text-sm text-red-500 hover:text-red-700 disabled:opacity-50"
              >
                {unlinkingTelegram ? 'Unlinking…' : 'Unlink Telegram'}
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Link your Telegram account to manage assets and transactions via the bot.
              </p>
              <button
                onClick={() => void generateTelegramLink()}
                disabled={telegramLoading}
                className="rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                {telegramLoading ? 'Generating…' : 'Generate Link Token'}
              </button>
              {telegramToken && telegramUrl && (
                <div className="rounded-lg bg-blue-50 p-3 text-sm space-y-2">
                  <p className="font-medium text-blue-800">Step 1 — Open the link or copy the bot command:</p>
                  <button
                    onClick={() => void copyCommand()}
                    className="flex w-full items-center justify-between gap-2 rounded-lg border border-blue-200 bg-white px-3 py-2 font-mono text-sm hover:bg-blue-50"
                  >
                    <code className="break-all text-left text-gray-800">/link {telegramToken}</code>
                    <span className="shrink-0 text-xs text-primary-600">{tokenCopied ? '✓ Copied' : 'Copy'}</span>
                  </button>
                  <p className="font-medium text-blue-800">Step 2 — Send that command to the Telegram bot.</p>
                  <p className="text-xs text-blue-500">⏱ Expires in 10 minutes.</p>
                  <p className="text-blue-700">
                    Or open:{' '}
                    <a href={telegramUrl} className="break-all underline" target="_blank" rel="noreferrer">
                      {telegramUrl}
                    </a>
                  </p>
                </div>
              )}
            </div>
          )}
        </Section>

        {/* Google Sheets Integration */}
        <Section title="Google Sheets">
          <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
            Connect your Google account to automatically sync all assets and transactions to a Google Spreadsheet.
            Any changes you make here will reflect in the sheet within seconds.
          </p>

          {sheetsLoading ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : sheetsStatus?.connected ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
                <span>✓</span> Connected to Google Sheets
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-700/50 p-3 text-sm space-y-1">
                <p>
                  <span className="font-medium text-gray-700 dark:text-gray-300">Spreadsheet: </span>
                  <a
                    href={sheetsStatus.spreadsheetUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary-600 hover:underline break-all"
                  >
                    Open in Google Sheets ↗
                  </a>
                </p>
                <p className="text-gray-500 dark:text-gray-400">
                  Connected {new Date(sheetsStatus.connectedAt).toLocaleDateString()}
                </p>
                {sheetsStatus.lastSyncAt && (
                  <p className="text-gray-500 dark:text-gray-400">
                    Last synced {new Date(sheetsStatus.lastSyncAt).toLocaleString()}
                  </p>
                )}
                {sheetsStatus.lastSyncError && (
                  <p className="text-xs text-red-500">
                    Last sync error: {sheetsStatus.lastSyncError}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => void handleSheetsSync()}
                  disabled={sheetsSyncing}
                  className="rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                >
                  {sheetsSyncing ? 'Syncing…' : 'Sync Now'}
                </button>
                <button
                  onClick={() => void handleSheetsDisconnect()}
                  disabled={sheetsDisconnecting}
                  className="text-sm text-red-500 hover:text-red-700 disabled:opacity-50 px-2"
                >
                  {sheetsDisconnecting ? 'Disconnecting…' : 'Disconnect'}
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => { window.location.href = sheetsApi.connectUrl(); }}
              className="flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" />
              </svg>
              Connect Google Sheets
            </button>
          )}
        </Section>

        {/* Custom Currencies */}
        <Section title="Custom Currencies">
          <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
            Define virtual currency units like "Thousand VND" to use on your assets and transactions.
          </p>

          {/* Existing list */}
          {customCurrencies.length > 0 && (
            <table className="mb-4 w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase tracking-wide text-gray-400">
                  <th className="pb-1 pr-3">Code</th>
                  <th className="pb-1 pr-3">Name</th>
                  <th className="pb-1 pr-3">Symbol</th>
                  <th className="pb-1 pr-3">Base</th>
                  <th className="pb-1 pr-3">Multiplier</th>
                  <th className="pb-1" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {customCurrencies.map((cc) => (
                  <tr key={cc.id}>
                    <td className="py-1.5 pr-3 font-mono font-medium text-gray-900 dark:text-gray-100">{cc.code}</td>
                    <td className="py-1.5 pr-3 text-gray-700 dark:text-gray-300">{cc.name}</td>
                    <td className="py-1.5 pr-3 text-gray-500 dark:text-gray-400">{cc.symbol ?? '—'}</td>
                    <td className="py-1.5 pr-3 text-gray-500 dark:text-gray-400">{cc.baseCode}</td>
                    <td className="py-1.5 pr-3 text-gray-500 dark:text-gray-400">×{cc.multiplier.toLocaleString()}</td>
                    <td className="py-1.5 text-right">
                      <button
                        onClick={() => void handleDeleteCurrency(cc.id)}
                        className="text-xs text-red-400 hover:text-red-600"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Add form */}
          <form onSubmit={(e) => void handleAddCurrency(e)} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Code (e.g. KVND)">
                <input
                  required
                  value={ccForm.code}
                  onChange={(e) => setCcForm({ ...ccForm, code: e.target.value.toUpperCase() })}
                  maxLength={10}
                  placeholder="KVND"
                  className={inp}
                />
              </Field>
              <Field label="Name (e.g. Thousand VND)">
                <input
                  required
                  value={ccForm.name}
                  onChange={(e) => setCcForm({ ...ccForm, name: e.target.value })}
                  placeholder="Thousand VND"
                  className={inp}
                />
              </Field>
              <Field label="Symbol (optional, e.g. K₫)">
                <input
                  value={ccForm.symbol}
                  onChange={(e) => setCcForm({ ...ccForm, symbol: e.target.value })}
                  placeholder="K₫"
                  className={inp}
                />
              </Field>
              <Field label="Base ISO Currency">
                <input
                  required
                  value={ccForm.baseCode}
                  onChange={(e) => setCcForm({ ...ccForm, baseCode: e.target.value.toUpperCase() })}
                  maxLength={10}
                  placeholder="VND"
                  className={inp}
                />
              </Field>
              <Field label="Multiplier (1 unit = ? base units)">
                <input
                  required
                  type="number"
                  min="0.000001"
                  step="any"
                  value={ccForm.multiplier}
                  onChange={(e) => setCcForm({ ...ccForm, multiplier: e.target.value })}
                  placeholder="1000"
                  className={inp}
                />
              </Field>
            </div>
            {ccError && <p className="text-xs text-red-500">{ccError}</p>}
            <button
              type="submit"
              disabled={ccSaving || isViewer}
              title={isViewer ? 'Viewers cannot add currencies' : undefined}
              className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {ccSaving ? 'Adding…' : '+ Add Currency'}
            </button>
          </form>
        </Section>

        {/* Categories */}
        <Section title="Categories">
          <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
            Organise your transactions. Categories power the spending breakdown on your dashboard.
          </p>

          {/* Existing list grouped by type */}
          {(['expense', 'income', 'any'] as const).map((group) => {
            const items = categories.filter((cat) => cat.type === group);
            if (items.length === 0) return null;
            return (
              <div key={group} className="mb-4">
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">
                  {group === 'any' ? 'Any type' : group}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {items.map((cat) => (
                    <span
                      key={cat.id}
                      className="group inline-flex items-center gap-1.5 rounded-full border border-gray-200 dark:border-gray-700 py-1 pl-2 pr-1 text-sm text-gray-700 dark:text-gray-300"
                    >
                      <span
                        className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ background: cat.color }}
                      />
                      {cat.icon && <span>{cat.icon}</span>}
                      <span>{cat.name}</span>
                      <button
                        onClick={() => void handleDeleteCategory(cat.id)}
                        disabled={isViewer}
                        title={isViewer ? 'Viewers cannot delete categories' : 'Delete category'}
                        className="ml-0.5 flex h-5 w-5 items-center justify-center rounded-full text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gray-400"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Add form */}
          <form onSubmit={(e) => void handleAddCategory(e)} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name">
                <input
                  required
                  value={catForm.name}
                  onChange={(e) => setCatForm({ ...catForm, name: e.target.value })}
                  placeholder="e.g. Groceries"
                  className={inp}
                />
              </Field>
              <Field label="Type">
                <select
                  value={catForm.type}
                  onChange={(e) => setCatForm({ ...catForm, type: e.target.value as TransactionType | 'any' })}
                  className={inp}
                >
                  <option value="expense">Expense</option>
                  <option value="income">Income</option>
                  <option value="any">Any</option>
                </select>
              </Field>
              <Field label="Icon (emoji, optional)">
                <input
                  value={catForm.icon}
                  onChange={(e) => setCatForm({ ...catForm, icon: e.target.value })}
                  placeholder="🍔"
                  maxLength={4}
                  className={inp}
                />
              </Field>
              <Field label="Colour">
                <input
                  type="color"
                  value={catForm.color}
                  onChange={(e) => setCatForm({ ...catForm, color: e.target.value })}
                  className="h-10 w-full cursor-pointer rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/50 p-1"
                />
              </Field>
            </div>
            {catError && <p className="text-xs text-red-500">{catError}</p>}
            <button
              type="submit"
              disabled={catSaving || isViewer}
              title={isViewer ? 'Viewers cannot add categories' : undefined}
              className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {catSaving ? 'Adding…' : '+ Add Category'}
            </button>
          </form>
        </Section>

        {/* Profile */}
        <Section title="Profile">
          <form onSubmit={(e) => void handleProfileSave(e)} className="space-y-4">
            <Field label="Display Name">
              <input
                type="text"
                required
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className={inp}
              />
            </Field>
            <div className="space-y-1 text-sm text-gray-500 dark:text-gray-400">
              <p><span className="font-medium text-gray-700 dark:text-gray-300">Email:</span> {user?.email}</p>
              <p><span className="font-medium text-gray-700 dark:text-gray-300">Member since:</span> {user?.createdAt.slice(0, 10)}</p>
              <p><span className="font-medium text-gray-700 dark:text-gray-300">Role:</span> <span className="capitalize">{user?.role}</span></p>
            </div>
            <button
              type="submit"
              disabled={profileSaving || isViewer}
              title={isViewer ? 'Viewers cannot update profile' : undefined}
              className="rounded-lg bg-primary-600 px-5 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {profileSaving ? 'Saving…' : 'Update Name'}
            </button>
          </form>
        </Section>

        {/* Security */}
        <Section title="Security">
          {/* Change Password */}
          <div className="mb-6">
            <h3 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-300">Change Password</h3>
            <form onSubmit={(e) => void handleChangePassword(e)} className="space-y-3">
              <Field label="Current Password">
                <input type="password" required value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} className={inp} autoComplete="current-password" />
              </Field>
              <Field label="New Password">
                <input type="password" required value={pwForm.next} onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })} className={inp} autoComplete="new-password" />
              </Field>
              <Field label="Confirm New Password">
                <input type="password" required value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} className={inp} autoComplete="new-password" />
              </Field>
              {pwError && <p className="text-xs text-red-500">{pwError}</p>}
              <button type="submit" disabled={pwSaving} className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50">
                {pwSaving ? 'Saving…' : 'Change Password'}
              </button>
            </form>
          </div>

          {/* Linked accounts */}
          {googleLinked && (
            <div className="mb-6 border-t border-gray-100 dark:border-gray-700 pt-5">
              <h3 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-300">Linked Accounts</h3>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Google</span>
                <button onClick={() => void handleUnlinkGoogle()} disabled={unlinkingGoogle} className="text-red-500 hover:text-red-700 disabled:opacity-50 text-xs">
                  {unlinkingGoogle ? 'Unlinking…' : 'Unlink'}
                </button>
              </div>
            </div>
          )}

          {/* Active sessions */}
          <div className="mb-6 border-t border-gray-100 dark:border-gray-700 pt-5">
            <h3 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-300">Active Sessions</h3>
            {sessionsLoading ? (
              <p className="text-sm text-gray-400">Loading…</p>
            ) : sessions.length === 0 ? (
              <p className="text-sm text-gray-400">No sessions found.</p>
            ) : (
              <ul className="space-y-2">
                {sessions.map((s) => (
                  <li key={s.sessionId} className="flex items-start justify-between gap-2 rounded-lg border border-gray-100 dark:border-gray-700 p-3 text-xs">
                    <div className="min-w-0">
                      <p className="truncate font-medium text-gray-800 dark:text-gray-200">
                        {s.userAgent ? s.userAgent.slice(0, 60) : 'Unknown browser'}
                        {s.isCurrent && <span className="ml-2 rounded bg-primary-100 dark:bg-primary-900/40 px-1.5 py-0.5 text-primary-700 dark:text-primary-300">current</span>}
                      </p>
                      <p className="text-gray-400">Started {new Date(s.createdAt).toLocaleDateString()}</p>
                    </div>
                    {!s.isCurrent && (
                      <button onClick={() => void handleRevokeSession(s.sessionId)} className="shrink-0 text-red-500 hover:text-red-700">
                        Revoke
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Delete account */}
          <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 p-4">
            <h3 className="mb-2 text-sm font-semibold text-red-700 dark:text-red-400">Delete Account</h3>
            <p className="mb-3 text-xs text-red-600 dark:text-red-400">
              This permanently deletes all your data including assets, transactions, and settings. This cannot be undone.
            </p>
            <form onSubmit={(e) => void handleDeleteAccount(e)} className="space-y-3">
              <Field label="Type DELETE to confirm">
                <input value={deleteConfirmText} onChange={(e) => setDeleteConfirmText(e.target.value)} placeholder="DELETE" className={inp} />
              </Field>
              {hasPassword && (
                <Field label="Your Password">
                  <input type="password" required value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} className={inp} autoComplete="current-password" />
                </Field>
              )}
              {deleteError && <p className="text-xs text-red-500">{deleteError}</p>}
              <button type="submit" disabled={deletingAccount || deleteConfirmText !== 'DELETE'} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50">
                {deletingAccount ? 'Deleting…' : 'Delete My Account'}
              </button>
            </form>
          </div>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-sm">
      <h2 className="mb-4 text-base font-semibold text-gray-800 dark:text-gray-200">{title}</h2>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
      {children}
    </div>
  );
}

const inp = 'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 outline-none placeholder:text-gray-400 dark:placeholder-gray-500 focus:border-primary-500 focus:ring-2 focus:ring-primary-100 dark:focus:ring-primary-900/30 dark:[color-scheme:dark]';
